import { ActionFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import { CalendarState } from '../../controller';
import { CalendarContext } from '../../../../utils/context/contextFactory';
import { getBookingPreferencesForSelectedTime } from '../../../../utils/bookingPreferences/bookingPreferencesForSelectedTime';
import { updateCalendarErrors } from './calendarErrorsHandler';
import { SlotAvailability } from '@wix/ambassador-availability-calendar/types';
import { ServiceType } from '@wix/bookings-uou-types';
import { GetActiveFeaturesResponse } from '@wix/ambassador-services-catalog-server/types';
import {
  DialogState,
  DialogType,
} from '../../ViewModel/dialogViewModel/dialogViewModel';
import { SetFocusedElement } from '../setFocusedElement/setFocusedElement';
import { FlowElements } from '../../Hooks/useFlow';
import { isWaitingListFlow } from '../../../../utils/waitingList/waitingList';
import { AddError } from '../addError/addError';
import { getSelectedSlots } from '../../../../utils/selectedSlots/selectedSlots';
import { CalendarErrors } from '../../../../types/types';
import { BOOKINGS_CALENDAR_REFERRAL_INFO } from '../../../../constants/constants';

export const submitErrors = [
  CalendarErrors.SELECTED_SLOT_VALIDATION_NO_SELECTED_LOCATION,
  CalendarErrors.SELECTED_SLOT_VALIDATION_NO_SELECTED_DURATION,
  CalendarErrors.SELECTED_SLOT_VALIDATION_NO_SELECTED_STAFF_MEMBER,
  CalendarErrors.SELECTED_SLOT_VALIDATION_NO_TIME_SELECTED_ERROR,
];
export type OnSubmit = () => void;

function isBookingsEnabled(
  serviceType: ServiceType,
  activeFeatures: GetActiveFeaturesResponse,
): boolean {
  switch (serviceType) {
    case ServiceType.GROUP:
      return !!activeFeatures.applicableForGroups;
    case ServiceType.INDIVIDUAL:
      return !!activeFeatures.applicableForIndividual;
    default:
      return true;
  }
}

export function createOnSubmitAction(
  actionFactoryParams: ActionFactoryParams<CalendarState, CalendarContext>,
  addError: AddError,
  setFocusedElement: SetFocusedElement,
): OnSubmit {
  return async () => {
    const { getControllerState, context } = actionFactoryParams;
    const [state, setState] = getControllerState();
    const { t, businessInfo, biLogger, wixSdkAdapter, activeFeatures } =
      context;
    let {
      selectedTime,
      selectableSlotsAtSelectedTime,
      selectedBookingPreferences,
      calendarErrors,
      selectedService,
    } = state;
    const isBookingEnabled = isBookingsEnabled(
      selectedService!.info.type,
      activeFeatures!,
    );

    if (!selectedTime) {
      const calendarError =
        CalendarErrors.SELECTED_SLOT_VALIDATION_NO_TIME_SELECTED_ERROR;

      void biLogger.bookingsPaymentMethodSelectionNextClicked({
        userMessage: calendarError,
      });
      addError(calendarError);
      setFocusedElement(FlowElements.BOTTOM_SECTION);
    }

    if (selectableSlotsAtSelectedTime) {
      const bookingPreferences = getBookingPreferencesForSelectedTime({
        selectableSlotsAtSelectedTime,
        calendarErrors,
        selectedBookingPreferences,
        context,
      });

      updateCalendarErrors(
        bookingPreferences,
        addError,
        selectedBookingPreferences,
      );

      [{ calendarErrors }] = getControllerState();

      const isCalendarErrorsHasSubmitErrors = calendarErrors.some((error) =>
        submitErrors.includes(error),
      );

      const dateRegionalSettingsLocale =
        businessInfo!.dateRegionalSettingsLocale!;
      const selectedSlots = getSelectedSlots({
        selectableSlotsAtSelectedTime,
        dateRegionalSettingsLocale,
        t,
        selectedBookingPreferences,
      });
      const isWaitingList = isWaitingListFlow({
        selectableSlots: selectableSlotsAtSelectedTime,
        selectedBookingPreferences,
        bookingPreferences,
      });

      if (!isCalendarErrorsHasSubmitErrors && selectedSlots.length > 0) {
        if (wixSdkAdapter.isPreviewMode()) {
          if (!isBookingEnabled) {
            await wixSdkAdapter.openPreviewPremiumModal(
              selectedService!.info.type,
              BOOKINGS_CALENDAR_REFERRAL_INFO,
            );
          }
          return goToNextPage(
            actionFactoryParams,
            selectedSlots,
            isWaitingList,
          );
        }
        if (wixSdkAdapter.isSiteMode()) {
          if (isBookingEnabled) {
            return goToNextPage(
              actionFactoryParams,
              selectedSlots,
              isWaitingList,
            );
          } else {
            setState({
              dialog: {
                type: DialogType.PremiumViewer,
                state: DialogState.IDLE,
              },
            });
          }
        }
      } else {
        void biLogger.bookingsPaymentMethodSelectionNextClicked({
          selectedSlot: selectedTime,
          ...getBiProperties({ isWaitingList }),
        });
      }
    }
  };
}

export async function goToNextPage(
  actionFactoryParams: ActionFactoryParams<CalendarState, CalendarContext>,
  selectedSlots: SlotAvailability[],
  isWaitingList: boolean,
) {
  const {
    getControllerState,
    context: { biLogger, wixSdkAdapter },
  } = actionFactoryParams;
  const [state, setState] = getControllerState();
  const { selectedTime, selectedService, selectedTimezone } = state;
  const isRescheduling = !!state.rescheduleBookingDetails;
  void biLogger.bookingsContactInfoSaveSuccess({
    selectedSlot: selectedTime,
    ...getBiProperties({ isWaitingList }),
  });

  if (isRescheduling) {
    setState({
      dialog: {
        type: DialogType.RescheduleConfirm,
        state: DialogState.IDLE,
      },
    });
  } else if (isWaitingList) {
    setState({
      dialog: {
        type: DialogType.JoinWaitlist,
        state: DialogState.IDLE,
      },
    });
  } else {
    const slotAvailability = selectedSlots[0];
    const serviceId = selectedService!.id;
    const serviceSlug = selectedService!.info.slug;
    await wixSdkAdapter.navigateToBookingsFormPage({
      slotAvailability,
      serviceId,
      serviceSlug,
      referral: BOOKINGS_CALENDAR_REFERRAL_INFO,
      timezone: selectedTimezone,
    });
  }
}

const getBiProperties = ({ isWaitingList }: { isWaitingList: boolean }) => {
  return isWaitingList
    ? { properties: JSON.stringify({ waitlist: true }) }
    : {};
};
